"use client";

import { useEffect, useState } from "react";

interface UseViewportKeyboardReturn {
  isKeyboardOpen: boolean;
  keyboardHeight: number;
  visibleHeight: number;
}

/**
 * Detects virtual keyboard state using visualViewport API
 *
 * Handles iOS Safari viewport resize events and provides fallback
 * for browsers without visualViewport support.
 *
 * @returns Keyboard state for UI adjustments
 */
export function useViewportKeyboard(): UseViewportKeyboardReturn {
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [visibleHeight, setVisibleHeight] = useState(
    typeof window !== "undefined" ? window.innerHeight : 0
  );

  useEffect(() => {
    // SSR guard
    if (typeof window === "undefined") return;

    const updateViewport = () => {
      // Try visualViewport API first (better accuracy)
      if (window.visualViewport) {
        const vv = window.visualViewport;
        // window.innerHeight를 매번 현재값으로 읽어야 iOS 주소 표시줄 변화를 반영할 수 있음
        // vv.offsetTop까지 포함해야 스크롤 시에도 정확함
        const newKeyboardHeight = window.innerHeight - vv.height;

        setVisibleHeight(vv.height);
        setKeyboardHeight(Math.max(0, newKeyboardHeight));
        setIsKeyboardOpen(newKeyboardHeight > 100); // Threshold to avoid false positives
      } else {
        // Fallback: visualViewport 미지원 시 키보드 감지 불가
        setVisibleHeight(window.innerHeight);
        setKeyboardHeight(0);
        setIsKeyboardOpen(false);
      }
    };

    // Use visualViewport events if available
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", updateViewport);
      window.visualViewport.addEventListener("scroll", updateViewport);
    }

    // Fallback to window resize
    window.addEventListener("resize", updateViewport);

    // Initial check
    updateViewport();

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", updateViewport);
        window.visualViewport.removeEventListener("scroll", updateViewport);
      }
      window.removeEventListener("resize", updateViewport);
    };
  }, []);

  return { isKeyboardOpen, keyboardHeight, visibleHeight };
}
